import axios, { CanceledError } from "axios";

import { getApiUrlPrefix } from "./utilities";
import { ApiError, API_ERROR_KINDS } from "./apiError";

const API = axios.create({
  baseURL: getApiUrlPrefix(),
});

const transformAxiosResponse = (response) => {
  if (response.data) {
    if (response.data.data) {
      const { data, ...rest } = response.data;
      return {
        rows: data,
        ...rest,
      };
    }
    return response.data;
  }

  return response;
};

const catchAxiosError = (error) => {
  if (error instanceof CanceledError) {
    return;
  }

  // Classified before the message-shaping below, because these two are the ones
  // callers have to tell apart and the shaping below erases the difference.
  //
  // No response at all: the request was sent and nothing came back. A laptop
  // waking up cancels its in-flight requests as ERR_NETWORK_CHANGED, which
  // lands here. It is not evidence about the session or the user.
  if (!error.response && error.request) {
    throw new ApiError(error.message || "Network Error", {
      kind: API_ERROR_KINDS.NETWORK,
      code: error.code ?? null,
    });
  }

  // The server rejected the token. Only re-authentication fixes this, and it
  // must never be shown as "you don't have an account".
  if (error.response?.status === 401) {
    throw new ApiError("Session expired", {
      kind: API_ERROR_KINDS.SESSION_EXPIRED,
      status: 401,
    });
  }

  // The server answered and the record isn't there — the one failure that does
  // mean the user has no profile.
  if (error.response?.status === 404) {
    throw new ApiError(error.response.data?.message || "Not found", {
      kind: API_ERROR_KINDS.NOT_FOUND,
      status: 404,
    });
  }

  if (error.response) {
    // Matt: Sometimes the error is returned as a string under data
    // Matt: API team notified, they are working to resolve
    if (typeof error.response.data === "string") {
      throw Error(error.response.data);
    }

    if (error.response.data?.conflicts) {
      throw Error(error.response.data.conflicts[0]?.message ?? "Server Error");
    }

    // Sometimes the error is returned as an object with a message prop
    if (error.response.data?.message) {
      // Array of messages for validation
      if (error.response.data.details?.length) {
        throw error.response.data.details.map((detail) => {
          if (detail.param) {
            return `${detail.msg} for ${detail.param}`;
          } else {
            return `${detail.msg}`;
          }
        });
      }

      // Need message and resetLink if expired password error
      if (error.config?.url === "/login" && error.response.data.resetLink) {
        throw error.response.data;
      }

      throw error.response.data.message;
    }

    if (error.message) {
      throw Error(error.message);
    }

    if (error.response.data.errors) {
      throw Error(error.response.data.errors.map((e) => e.msg).join(", "));
    }

    throw Error(`${error.response.status} http status received`);
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    throw Error(error.message ?? "No response received");
  } else {
    // Something happened in setting up the request that triggered an Error
    throw Error(error.message ?? "Something went wrong");
  }
};

API.interceptors.response.use(transformAxiosResponse, catchAxiosError);

export default API;
